/**
 * Size of a connector pin on screen (pure, unit tested). A pin is page-relative: its diameter is the
 * connector's `size` (1/Q of page width, shared/annotations.ts) of the page box, so it covers the same
 * notes at any zoom and on any device; MIN_VISIBLE_PIN_PX keeps it a recognisable pin (with its arrow)
 * on small page boxes — landscape two-up and iPad mini portrait draw default pins at that minimum.
 * Used by the view-mode pin (Pins.tsx) and by edit mode (annotate/: markers, bounds, hit tests).
 */
import type { CSSProperties } from 'react';
import { DEFAULT_CONNECTOR_SIZE, Q } from '../../shared/annotations';

/** Smallest pin drawn (CSS px), whatever its size and the zoom (still well under the old 36 px pin). */
export const MIN_VISIBLE_PIN_PX = 16;
/** Smallest label text (CSS px): the label keeps the old pins' 14 px, also on a small pin. */
export const MIN_PIN_LABEL_PX = 14;

/** Diameter (CSS px) of a pin of `size` (absent: DEFAULT_CONNECTOR_SIZE) on a page box `boxW` px wide. */
export function pinDiameterPx(size: number | undefined, boxW: number): number {
  return Math.max(MIN_VISIBLE_PIN_PX, ((size ?? DEFAULT_CONNECTOR_SIZE) / Q) * Math.max(0, boxW));
}

export interface PinLook {
  /** Icon size (px, ≈ 60 % of the circle; the arrow is always drawn). */
  icon: number;
  /** White ring around the circle (px). */
  ring: number;
  /** Label font size (px) and padding, and the gap between pin and label. */
  font: number;
  padX: number;
  padY: number;
  gap: number;
}

/** How a pin of diameter `d` px is drawn: a thinner ring on small pins, a label that grows with large ones. */
export function pinLook(d: number): PinLook {
  const font = Math.round(Math.min(16, Math.max(MIN_PIN_LABEL_PX, 0.7 * d)));
  return {
    icon: Math.round(d * 0.6),
    ring: d < 28 ? 1.5 : 2,
    font,
    padX: Math.round(font * 0.4),
    padY: Math.round(font * 0.12),
    gap: Math.max(3, Math.round(d * 0.15)),
  };
}

/**
 * Where a pin's label sits: beside the circle (`left`: on its left, near the page's right edge), and
 * counter-scaled during a pinch (--kgr-pin-scale) about the side facing the pin, so it keeps its
 * screen size while the pin follows the page and does not jump when the pinch ends.
 */
export function pinLabelPlacement(look: PinLook, left: boolean): CSSProperties {
  return {
    transform: 'scale(var(--kgr-pin-scale, 1))',
    transformOrigin: left ? 'right center' : 'left center',
    ...(left ? { right: '100%', marginRight: look.gap } : { left: '100%', marginLeft: look.gap }),
  };
}

/**
 * The pin a tap at (x, y) (CSS px in a page box w × h) is meant for: the nearest one. Pins' ≥ 44 pt
 * hit areas overlap where their (small) circles do not, so the top-most button is not always the dot
 * that was tapped.
 */
export function nearestPin<P extends { x: number; y: number }>(pins: readonly P[], x: number, y: number, w: number, h: number): P | null {
  let best: P | null = null;
  let bestD = Infinity;
  for (const p of pins) {
    const d = Math.hypot(p.x * w - x, p.y * h - y);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}
